import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { faPenNib, faCancel, faUsers, faUsersSlash } from '@fortawesome/pro-solid-svg-icons';
import { faEdit, faTrashAlt, faSave, faEye } from '@fortawesome/free-solid-svg-icons';
import CustomToast from '../../../../components/Toast';
import {
  useMoveQSetDocumentMutation
} from '../../../../features/authentication/qSetApiSlice';
import {
  useUpdateDescriptionMutation,
  useUpdateDocumentScopeMutation,
  useDeleteResourceMutation,
  useUpdateDocumentUrlMutation,
  useUpdateDocumentContentMutation
} from '../../../../features/authentication/teacherDocumentApiSlice';
import {
  useGetResourceDocMutation
} from '../../../../features/authentication/assignmentApislice';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConfirmationModal from '../../../../components/ConfirmationPopUp';
import Editor from './Editor';
import Modal from '../../../../components/Modal';
import VideoModal from './VideoModal';
import DocumentModal from './DocumentModal';
import AudioModal from './AudioModal';

const ResourceList = (props) => {
  const [isMouseOver, setIsMouseOver] = React.useState(false);
  const [moveQSetDocument] = useMoveQSetDocumentMutation();
  const [description, setDescription] = React.useState('');
  const [updateDescription] = useUpdateDescriptionMutation();
  const [documentText, setDocumentText] = React.useState('');
  const [videoModal, setVideoModal] = React.useState(false);
  const [videoUrl, setVideoUrl] = React.useState('');
  const [audioUrl, setAudioUrl] = React.useState('');
  const [documentModal, setDocumentModal] = React.useState(false);
  const [audioModal, setAudioModal] = React.useState(false);
  const [getResourceDoc] = useGetResourceDocMutation();
  const [updateScope] = useUpdateDocumentScopeMutation();
  const [url, setUrl] = React.useState('');
  const [openEditResource, setOpenEditResource] = React.useState(false);
  const [selectedResourceType, setSelectedResourceType] = React.useState(null);
  const [confirmModel, setConfirmModel] = React.useState(false);
  const [deleteResource] = useDeleteResourceMutation();
  const [updateDocumentUrl] = useUpdateDocumentUrlMutation();
  const [updateDocumentContent] = useUpdateDocumentContentMutation();
  const [isWaiting, setIsWaiting] = React.useState(false);

  const handleDocumentDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(props.documents);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (result.source.index != result.destination.index) {
      handleMoveDocument(parseInt(result.draggableId), result.source.index, result.destination.index);
      props.setDocuments(items);
    }
  };

  const handleMoveDocument = async (documentId, sourceIndex, destinationIndex) => {
    const option = {
      setId: props.selectedResourceSetId,
      sourceIndex: sourceIndex,
      destinationIndex: destinationIndex
    };

    try {
      const result = await moveQSetDocument(option);
      if (result.error) {
        CustomToast(result.error.data.error, 'error');
        props.setDocuments(props.documents);
      }
      else {
        CustomToast('Resource Moved Successfully', 'success');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRenameResource = async (documentId) => {
    let filteredResources = props.documents.filter(item => item.documentId == documentId && item.description == description);
    if (filteredResources.length > 0)
      return;

    await setIsWaiting(true);
    const option = {
      documentId: documentId,
      setId: props.selectedResourceSetId,
      description: description
    };

    try {
      const result = await updateDescription(option);
      if (result.error) {
        if (result.error.data.errors)
          CustomToast(result.error.data.errors.Description, 'error');
        else
          CustomToast(result.error.data.error, 'error');
      }
      else {
        CustomToast('Resource Name Updated Successfully', 'success');
        props.setIsUpdateDescription(false);
        let updatedDocumentList = props.documents.map(item => {
          if (item.documentId === documentId) {
            return { ...item, description: description };
          }
          return item;
        });
        props.setDocuments(updatedDocumentList);
      }
    } catch (error) {
      console.log(error);
    }
    await setIsWaiting(false);
  };

  const resourceViewHandler = async (resourceType, resourceId, resourceUrl) => {
    setDocumentText('');
    let link;
    switch (resourceType) {
    case 'Video':
      setVideoModal(true);
      setVideoUrl(resourceUrl);
      break;
    case 'Document':
      try {
        const option = { documentId: resourceId };
        setDocumentModal(true);
        const response = await getResourceDoc(option).unwrap();
        setDocumentText(response.resourceText);
      } catch (error) {
        console.error(error);
      }
      break;
    case 'Audio':
      setAudioModal(true);
      setAudioUrl(resourceUrl);
      break;
    case 'Link':
    case 'PDF':
    case 'MS Word':
    case 'Power Point':
      link = document.createElement('a');
      link.href = resourceUrl;
      link.target = '_blank';
      link.click();
      break;
    default:
      console.warn('Unknown resource type: ', resourceType);
    }
  };

  const handleUpdateDocumentScope = async (documentId, isPublic, description) => {
    const option = {
      documentId: documentId,
      isPublic: isPublic ? 1 : 0
    };

    try {
      const result = await updateScope(option);
      if (result.error) {
        CustomToast(result.error.data.error, 'error');
      }
      else {
        CustomToast(`${isPublic ? 'Enabled' : 'Disabled'}` + ` Public Scope for ${description}.`, 'success');

        let updatedDocumentList = props.documents.map(item => {
          if (item.documentId === documentId) {
            return { ...item, isPublic: isPublic };
          }
          return item;
        });
        props.setDocuments(updatedDocumentList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteResource = async () => {
    const option = {
      documentId: props.selectedResourceId,
      setId: props.selectedResourceSetId
    };

    try {
      const result = await deleteResource(option);
      if (result.error) {
        CustomToast(result.error.data.error, 'error');
      }
      else {
        CustomToast('Resource Deleted Successfully', 'success');
        setConfirmModel(false);

        let updatedDocumentList = props.documents.filter(item => item.documentId !== props.selectedResourceId);
        props.setDocuments(updatedDocumentList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateDocumentUrl = async () => {
    const option = {
      documentId: props.selectedResourceId,
      url: url
    };

    try {
      const result = await updateDocumentUrl(option);
      if (result.error) {
        if (result.error.data.errors)
          CustomToast(result.error.data.errors.Url, 'error');
        else
          CustomToast(result.error.data.error, 'error');
      }
      else {
        CustomToast('Url Changes Saved Successfully.', 'success');
        setOpenEditResource(false);

        let updatedDocumentList = props.documents.map(item => {
          if (item.documentId === props.selectedResourceId) {
            return { ...item, url: url };
          }
          return item;
        });
        props.setDocuments(updatedDocumentList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateDocumentContent = async () => {
    const option = {
      documentId: props.selectedResourceId,
      content: description
    };

    try {
      const result = await updateDocumentContent(option);
      if (result.error) {
        CustomToast(result.error.data.error, 'error');
      }
      else {
        CustomToast('Document Changes Saved Successfully.', 'success');
        setOpenEditResource(false);

        let updatedDocumentList = props.documents.map(item => {
          if (item.documentId === props.selectedResourceId) {
            return { ...item, document: description };
          }
          return item;
        });
        props.setDocuments(updatedDocumentList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    if (props.searchKeyword.length >= 1)
      searchHandler();
    else
      props.setDocuments(props.originalDocuments);
  }, [props.searchKeyword]);

  const searchHandler = async () => {
    let searchResult = props.originalDocuments.filter(item => {
      const words = item.description.toLowerCase().split(' ');
      return words.some(word => word.includes(props.searchKeyword.toLowerCase()));
    });
    props.setDocuments(searchResult);
  };

  return (
    <>
      <ConfirmationModal
        isOpen={confirmModel}
        onClose={() => setConfirmModel(false)}
        onSave={handleDeleteResource}
        contentWidth={'35%'}
        buttonPosition={'center'}
        content={
          `Are you sure that you want to remove the resource document named "${props.displayText}" ?`
        }
        cancelButton={'No'}
        confirmButton={'Yes'}
      />
      {!props.isLoadingDocuments && props.originalDocuments?.length > 0 &&
        <div className="pb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Type Resource Name to Search..."
            onChange={(e) => props.setSearchKeyword(e.target.value)}
          />
        </div>
      }
      <div>
        {props.isLoadingDocuments ?
          <>
            Loading Resources...
          </>
          :
          <>
            {props.documents?.length == 0 ?
              <>
                <p className='text-danger pt-5 cursor-none'>{props.searchKeyword != '' ? 'No Results Found!' : 'Set is currently Empty, use the Add buttons to add resources to the set.'}</p>
              </>
              :
              <>
                <DragDropContext onDragEnd={handleDocumentDragEnd}>
                  <Droppable droppableId='rows'>
                    {(provided) => (
                      <div className='document-list-scroll pb-5' {...provided.droppableProps} ref={provided.innerRef} onMouseEnter={() => setIsMouseOver(false)} onMouseLeave={() => setIsMouseOver(true)}>
                        {props.documents?.map((document, index) => (
                          <Draggable key={document.documentId} draggableId={`${document.documentId}`} index={index} isDragDisabled={props.isUpdateDescription}>
                            {(provided, snapshot) => (
                              <div key={'doc' + index} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                className={`d-flex p-2 mb-3 resource-block row ${snapshot.isDragging && 'drag-style'} ${(isMouseOver && snapshot.isDragging) && ' cursor-not-allowed'}`}>
                                <div className='d-flex align-items-baseline justify-content-between' style={{ maxWidth: '645px !important' }}>
                                  <div className='d-flex align-items-center text-truncate w-60 row'>
                                    {props.isUpdateDescription && document.documentId == props.selectedResourceId ?
                                      <>
                                        <input
                                          id="inputDescriptionText"
                                          autoComplete="off"
                                          className={`form-control content-print input-edit-document min-w-400 ${description.length > 100 && 'border-danger'}`}
                                          value={description}
                                          onChange={(e) => setDescription(e.target.value)}
                                        />
                                        {description.length > 100 &&
                                          <div className="text-danger row">Description has exceeded a limit of 100 characters!</div>
                                        }
                                      </>
                                      :
                                      <h5 className='text-truncate cursor-none mt-3'>{document.description}</h5>}
                                  </div>
                                  {!snapshot.isDragging &&
                                    <div className='gap-2 justify-content-between'>
                                      {props.isUpdateDescription && document.documentId == props.selectedResourceId ?
                                        <>
                                          <OverlayTrigger
                                            key="saveToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Save</Tooltip>}>
                                            <button
                                              id={'save' + index}
                                              className={`action-btn-tile green ${(description.length > 100 || isWaiting) && 'disabled'}`}
                                              onClick={() => handleRenameResource(document.documentId)}
                                            >
                                              <FontAwesomeIcon icon={faSave} />
                                            </button>
                                          </OverlayTrigger>
                                          <OverlayTrigger
                                            key="cancelToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Cancel</Tooltip>}>
                                            <button
                                              id={'cancel' + index}
                                              className="action-btn-tile red"
                                              onClick={() => { setDescription(''); props.setSelectedResourceId(document.documentId); props.setIsUpdateDescription(false); }}
                                            >
                                              <FontAwesomeIcon icon={faCancel} />
                                            </button>
                                          </OverlayTrigger>
                                        </>
                                        :
                                        <>
                                          <OverlayTrigger
                                            key="viewToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">View</Tooltip>}>
                                            <button
                                              id={'view' + index}
                                              className="action-btn-tile yellow"
                                              onClick={() => resourceViewHandler(document.type, document.documentId, document.url)}
                                            >
                                              <FontAwesomeIcon icon={faEye} />
                                            </button>
                                          </OverlayTrigger>
                                          <OverlayTrigger
                                            key="isPublicToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">{!document.isPublic ? 'Enable ' : 'Disable '} Public visibility</Tooltip>}>
                                            <button
                                              id={'isPublic' + index}
                                              className="action-btn-tile green"
                                              onClick={async () => { handleUpdateDocumentScope(document.documentId, !document.isPublic, document.description); }}
                                            >
                                              {document.isPublic ? <FontAwesomeIcon icon={faUsers} /> : <FontAwesomeIcon icon={faUsersSlash} />}
                                            </button>
                                          </OverlayTrigger>
                                          <OverlayTrigger
                                            key="editToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Edit</Tooltip>}>
                                            <button
                                              id={'edit' + index}
                                              className={`action-btn-tile blue ${!document.isEditable && 'disabled'}`}
                                              onClick={() => { props.setSelectedResourceId(document.documentId); setDescription(document.document); setUrl(document.url); setOpenEditResource(true); setSelectedResourceType(document.type); }}
                                            >
                                              <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                          </OverlayTrigger>
                                          <OverlayTrigger
                                            key="renameToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Rename</Tooltip>}>
                                            <button
                                              id={'rename' + index}
                                              className="action-btn-tile violet"
                                              onClick={async () => { props.setSelectedResourceId(document.documentId); setDescription(document.description); props.setIsUpdateDescription(true); }}
                                            >
                                              <FontAwesomeIcon icon={faPenNib} />
                                            </button>
                                          </OverlayTrigger>
                                          <OverlayTrigger
                                            key="deleteToolTip"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Delete</Tooltip>}>
                                            <button
                                              id={'delete' + index}
                                              className="action-btn-tile red"
                                              onClick={async () => { props.setSelectedResourceId(document.documentId); props.setDisplayText(document.description); setConfirmModel(true); }}
                                            >
                                              <FontAwesomeIcon icon={faTrashAlt} />
                                            </button>
                                          </OverlayTrigger>
                                        </>
                                      }
                                    </div>
                                  }
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            }
          </>
        }
      </div>
      <Modal
        isOpen={openEditResource}
        onClose={() => { setOpenEditResource(false); setDescription(''); setUrl(''); }}
        titleName={`${selectedResourceType == 'Document' ? 'Edit Document' : 'Edit Url'}`}
        contentWidth='800px'>
        <>
          {selectedResourceType == 'Link' &&
            <div className='pb-4'>
              <label className='pb-2'>
                {'URL (must start with http:// or https://)'}
              </label>
              <input
                className={`form-control content-print pt-2 ${url.length > 255 && 'border-danger'}`}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              {url.length > 255 &&
                <div className="text-danger">Url has exceeded a limit of 255 characters!</div>
              }
            </div>
          }
          {selectedResourceType == 'Document' &&
            <>
              <Editor
                description={description}
                setDescription={setDescription}
              ></Editor>
            </>
          }
          <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
            <button className={`btn btn-castleRed btn-width ${((selectedResourceType == 'Link' && url.length == 0) || url.length > 255) && 'disabled'}`} onClick={() => { selectedResourceType == 'Link' ? handleUpdateDocumentUrl() : handleUpdateDocumentContent(); }}>
              Save
            </button>
            <button className="btn btn-castleGray btn-width" onClick={() => { setOpenEditResource(false); setDescription(''); setUrl(''); }}>
              Cancel
            </button>
          </div>
        </>
      </Modal>
      <VideoModal videoUrl={videoUrl} isOpen={videoModal} onClose={() => setVideoModal(false)} />
      <DocumentModal value={documentText} isOpen={documentModal} onClose={() => setDocumentModal(false)} />
      <AudioModal audioUrl={audioUrl} isOpen={audioModal} onClose={() => setAudioModal(false)} />
    </>
  );
};

ResourceList.propTypes = {
  originalDocuments: PropTypes.object,
  documents: PropTypes.object,
  setDocuments: PropTypes.func,
  setDisplayText: PropTypes.func,
  displayText: PropTypes.string,
  selectedResourceSetId: PropTypes.number,
  isLoadingDocuments: PropTypes.bool,
  isUpdateDescription: PropTypes.bool,
  setIsUpdateDescription: PropTypes.func,
  searchKeyword: PropTypes.string,
  setSearchKeyword: PropTypes.func,
  selectedResourceId: PropTypes.number,
  setSelectedResourceId: PropTypes.func,
};

export default ResourceList;